// Mapa tipado de estado-chip.tsx (plan_migracion_reui.md, R3-C — DP-R3-a):
// vive en su propio .ts (no en el .tsx) porque exporta constantes y
// funciones puras, no un componente (regla §1.15 del plan,
// react-refresh/only-export-components).
//
// Colores de Paleta B (punto 15 de la validación v0.9.0, hex cerrados por un
// panel de accesibilidad de 4 lentes — AA real, semántica/continuidad con el
// legacy, jerarquía del ciclo, daltonismo — ver
// context/design_validacion_v080_feedback.md, sección "Paleta B"):
//   - A chequear/A calcular: borde punteado `warning/75` en light (sube de
//     /55: 2.21:1 fallaba AA; dark queda /55), SIN tint de fondo — es la
//     tarea PENDIENTE, no un estado "logrado", con ícono de alerta.
//   - Sin salto: gris dedicado del chip (#696969 light / #a1a1aa dark) — el
//     DimGrey legacy literal, NO el token global --text-tertiary.
//   - Saltó/Calculado: tint naranja de marca intacto, texto `--salta` (deja
//     accent-strong para el círculo sólido del stepper) — accent-strong como
//     texto de chip fallaba AA (3.1:1).
//   - Entregado a obra/al cliente: `--info` (valor light nuevo en index.css).
//   - Aprobado: token propio `--aprobado` (tint 10%, familia teal) — separado
//     de `--success` genérico (ese sigue siendo para pills de hito/monto,
//     badge Activo, semáforos).
//   - A la espera de póliza/Póliza EC enviada al cliente: `--warning` (mismo
//     tono que el step "poliza" del stepper, ver STEP_COLOR_CLASS en
//     ciclo-vida-colores.ts; valor light nuevo en index.css).
//   - Facturado: ÚNICO chip SÓLIDO (condición del lente daltonismo — nunca
//     debe haber un segundo), token propio `--facturado`/`--facturado-
//     foreground`, SIEMPRE con el tilde (Check) — es lo que lo separa de
//     todo bajo cualquier simulación CVD.
export type VarianteEstadoRdt = 'chequear' | 'neutro' | 'salto' | 'info' | 'aprobado' | 'warning' | 'facturado';

// Estado (tal como llega en RdtResumen/RdtDetalle.estado) -> variante visual.
// Todo estado real conocido tiene entrada explícita; uno desconocido cae al
// neutro gris (ver varianteEstadoRdt más abajo) — cubierto por
// estado-chip-variantes.test.ts.
export const VARIANTE_ESTADO_RDT: Record<string, VarianteEstadoRdt> = {
  'A chequear': 'chequear',
  // Definitivas: mismo par de estados iniciales que Provisorias, espejo por
  // diseño (Juan 2026-07-11) — "A calcular" = "A chequear" (nadie la miró
  // todavía), "Calculado" = "Saltó" (≥1 monto informado, validada para
  // seguir el ciclo). Tratamiento visual IDÉNTICO: mismas variantes.
  'A calcular': 'chequear',
  Calculado: 'salto',
  'Sin salto': 'neutro',
  Saltó: 'salto',
  'Entregado a obra': 'info',
  'Entregado al cliente': 'info',
  Aprobado: 'aprobado',
  'A la espera de póliza': 'warning',
  // Nombre real del hito (migración 012_seed_hitos.sql) que llega como
  // `estado` cuando es el último completado — sin esta entrada caía al
  // neutro gris de "estado desconocido" (hallazgo del review adversarial
  // 2026-07-10).
  'Póliza EC enviada al cliente': 'warning',
  Facturado: 'facturado',
};

// Etiqueta que reemplaza a `estado` cuando el caller pasa `esperandoPoliza`
// en true (Aprobado completado + hito "Póliza EC enviada al cliente"
// aplicable todavía sin fecha — backend/src/domain/rdt-estado.ts
// derivarEstado). El backend nunca emite esto como `estado` (solo como el
// booleano `esperandoPoliza`): sin este pisado, una RDT aprobada con póliza
// pendiente se mostraba igual que una sin póliza — "Aprobado" a secas
// (hallazgo del review adversarial 2026-07-10).
export const ESTADO_ESPERANDO_POLIZA = 'A la espera de póliza';

// Variante -> clases Tailwind (Badge las recibe por className, R3-C: Badge
// radius="full" como base, colores de Paleta B tal cual el chip a mano de
// antes — ninguno es una variante estándar de reui/badge).
export const VARIANT_CLASS: Record<VarianteEstadoRdt, string> = {
  chequear: 'border border-dashed border-warning/75 bg-transparent text-warning dark:border-warning/55',
  // Gris DEDICADO del chip (NO --text-tertiary global, punto 15): el DimGrey
  // legacy literal pasa AA en light (4.82:1) mejor que cualquier candidato
  // nuevo; en dark, #a1a1aa (5.19:1 — el anterior, heredado de
  // --text-tertiary, daba 3.89:1 y fallaba).
  neutro: 'border border-transparent bg-surface-2 text-[#696969] dark:text-[#a1a1aa]',
  salto: 'border border-transparent bg-primary/11 text-salta',
  info: 'border border-transparent bg-info/10 text-info',
  aprobado: 'border border-transparent bg-aprobado/10 text-aprobado',
  warning: 'border border-transparent bg-warning/10 text-warning',
  facturado: 'border border-transparent bg-facturado text-facturado-foreground',
};

/** Etiqueta mostrada — «A la espera de póliza» pisa el `estado` real cuando
 *  `esperandoPoliza` (RdtEstadoDerivado.esperandoPoliza) es true. */
export function etiquetaEstadoRdt(estado: string, esperandoPoliza?: boolean): string {
  return esperandoPoliza ? ESTADO_ESPERANDO_POLIZA : estado;
}

/** Variante visual de un estado — misma regla de pisado que
 *  etiquetaEstadoRdt (ver EstadoChip en estado-chip.tsx). Un estado
 *  desconocido cae al neutro gris. */
export function varianteEstadoRdt(estado: string, esperandoPoliza?: boolean): VarianteEstadoRdt {
  return VARIANTE_ESTADO_RDT[etiquetaEstadoRdt(estado, esperandoPoliza)] ?? 'neutro';
}
